// Types, styles and formatters shared by the shipments table (envois-console) and board (envois-kanban).

export type LogiStatus =
  | "Attente dépôt"
  | "Réceptionné"
  | "Expédié"
  | "En transit"
  | "Dédouanement"
  | "Chez le correspondant"
  | "Livré"
  | "Annulé";

export type StageKey =
  | "prospect"
  | "paid"
  | "confirmed"
  | "shipped"
  | "transit"
  | "correspondent"
  | "delivered"
  | "cancelled";

export interface ShipmentItem {
  id: string;
  code: string;
  createdBy: string;
  createdAt: string;
  lead: string;
  awb: string;
  awbBy: string;
  awbAt: string;
  sender: string;
  phone: string;
  receiver: string;
  receiverPhone: string;
  from: string;
  fromC: string;
  to: string;
  toC: string;
  corridor: string;
  mode: "Aérien" | "Maritime" | "Express" | "Urbain";
  depart: string;
  departInfo: string;
  eta: string;
  colis: number;
  poids: number;
  nature: string;
  valeur: number;
  pay: "Payé" | "Non payé" | "Partiel" | "Remboursé";
  refOM: string;
  refTouch: string;
  canal: string;
  montant: number;
  paid: number;
  confirmed: boolean;
  logi: LogiStatus;
  logiInfo: string;
  sla: string;
  slaWarn: boolean;
  audit: string;
  note: string;
  // Board column, set once the shipment has been moved on the board; otherwise derived from its statuses.
  stage?: StageKey;
}

export const PAY_STYLE: Record<string, { bg: string; fg: string }> = {
  "Payé": { bg: "#d1fae5", fg: "#047857" },
  "Non payé": { bg: "#fee2e2", fg: "#b91c1c" },
  "Partiel": { bg: "#fef3c7", fg: "#b45309" },
  "Remboursé": { bg: "#e5e7eb", fg: "#4b5563" },
};

export const LOGI_STYLE: Record<LogiStatus, { bg: string; fg: string; dot: string }> = {
  "Attente dépôt": { bg: "#fff7ed", fg: "#c2410c", dot: "#fb923c" },
  "Réceptionné": { bg: "#ede9fe", fg: "#5b21b6", dot: "#7c3aed" },
  "Expédié": { bg: "#e0e7ff", fg: "#3730a3", dot: "#6366f1" },
  "En transit": { bg: "#e0f2fe", fg: "#0369a1", dot: "#0ea5e9" },
  "Dédouanement": { bg: "#fef3c7", fg: "#b45309", dot: "#f59e0b" },
  "Chez le correspondant": { bg: "#ccfbf1", fg: "#0f766e", dot: "#14b8a6" },
  "Livré": { bg: "#d1fae5", fg: "#047857", dot: "#10b981" },
  "Annulé": { bg: "#f1f5f9", fg: "#64748b", dot: "#94a3b8" },
};

export const MODE_STYLE: Record<string, { bg: string; fg: string }> = {
  "Aérien": { bg: "#e0f2fe", fg: "#0369a1" },
  "Maritime": { bg: "#e0e7ff", fg: "#3730a3" },
  "Express": { bg: "#ffe4e6", fg: "#be123c" },
  "Urbain": { bg: "#dcfce7", fg: "#15803d" },
};

export const toFloat = (val: any, fallback = 0): number => {
  if (val === null || val === undefined || val === "") return fallback;
  if (typeof val === "number") return isNaN(val) ? fallback : val;
  const parsed = parseFloat(String(val).replace(",", ".").replace(/\s/g, ""));
  return isNaN(parsed) ? fallback : parsed;
};

export const money = (n: number | string | null | undefined): string => {
  const val = toFloat(n, 0);
  return (
    val.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).replace(/[  ]/g, " ") + " FCFA"
  );
};

export const kg = (n: number | string | null | undefined): string => {
  const val = toFloat(n, 0);
  return (
    val.toLocaleString("fr-FR", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 2,
    }).replace(/[  ]/g, " ") + " kg"
  );
};

const FR_MONTHS: Record<string, number> = {
  janv: 0, févr: 1, mars: 2, avr: 3, mai: 4, juin: 5,
  juil: 6, août: 7, sept: 8, oct: 9, nov: 10, déc: 11,
};

// Parses "sam. 26 sept. 2026 · 09:12" (createdAt format). Returns null when unparseable.
export function parseFrDateTime(s: string): Date | null {
  const m = s.match(/(\d{1,2})\s+([a-zéû]+)\.?\s+(\d{4})(?:\s*·\s*(\d{1,2}):(\d{2}))?/i);
  if (!m) return null;
  const month = FR_MONTHS[m[2].toLowerCase()];
  if (month === undefined) return null;
  return new Date(Number(m[3]), month, Number(m[1]), Number(m[4] ?? 0), Number(m[5] ?? 0));
}

// Formats a Date back into the createdAt style, e.g. "lun. 28 sept. 2026 · 14:05".
export function formatFrDateTime(d: Date): string {
  const date = d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return `${date} · ${time}`;
}
